/* eslint-disable @typescript-eslint/prefer-includes */
export function readThemeMode(): 'system' | 'dark' | 'light' {
  const savedThemeMode = localStorage.getItem('rbb-theme-mode');
  if (savedThemeMode === 'light') {
    return 'light';
  }
  if (savedThemeMode === 'dark') {
    return 'dark';
  }
  return 'system';
}

export function calculateTheme(mode: 'system' | 'dark' | 'light'): 'dark' | 'light' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  if (mode === 'light') {
    return 'light';
  }
  return 'dark';
}

export function applyThemeMode(mode: 'system' | 'dark' | 'light'): void {
  document.documentElement.setAttribute('data-bs-theme-mode', mode);
  document.documentElement.setAttribute('data-bs-theme', calculateTheme(mode));
}

export function setThemeMode(mode: 'system' | 'dark' | 'light'): void {
  if (mode === 'system') {
    localStorage.removeItem('rbb-theme-mode');
  } else {
    localStorage.setItem('rbb-theme-mode', mode);
  }
  applyThemeMode(readThemeMode());
}

applyThemeMode(readThemeMode());

(window as unknown as { applyThemeMode: (mode: 'system' | 'dark' | 'light') => void }).applyThemeMode = applyThemeMode;
(window as unknown as { setThemeMode: (mode: 'system' | 'dark' | 'light') => void }).setThemeMode = setThemeMode;

export function getIdentifiedBrowserName(): string {
  const userAgentData = (window.navigator as Navigator & { userAgentData: { brands: { brand: string | undefined }[] | undefined; mobile: boolean | undefined } | undefined }).userAgentData;
  if (userAgentData) {
    const brands = userAgentData.brands?.map(brand => brand.brand)?.filter(s => s) as string[] ?? [];
    if (brands.includes('Microsoft Edge')) {
      return 'Edge Chromium';
    }
    if (brands.includes('Google Chrome')) {
      return 'Chrome';
    }
    if (brands.includes('Mozilla Firefox')) {
      return 'Firefox';
    }
    if (brands.includes('Apple Safari')) {
      return userAgentData.mobile ? 'Safari iOS' : 'Safari';
    }
  }

  let ua = window.navigator.userAgent;

  if (ua.indexOf('MSIE ') !== -1 || ua.indexOf('Trident/') !== -1) {
    return 'Internet Explorer';
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    const edgeVersion = parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    if (edgeVersion >= 12 && edgeVersion < 70) {
      return 'Edge';
    }
  }

  const edgeChromium = ua.indexOf('Edg/');
  if (edgeChromium > 0) {
    if (parseInt(ua.substring(edge + 4, ua.indexOf('.', edge)), 10) >= 70) {
      return 'Edge Chromium';
    }
  }

  ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf('safari') === -1 && ua.indexOf('applewebkit') === -1 && ua.indexOf('chrome') === -1 && ua.indexOf('mobile') === -1 && ua.indexOf('android') === -1 && ua.indexOf('gecko') !== -1 && ua.indexOf('firefox') !== -1) {
    return 'Firefox';
  }

  if (ua.indexOf('safari') !== -1 && ua.indexOf('applewebkit') !== -1 && ua.indexOf('chrome') !== -1 && ua.indexOf('mobile') === -1 && ua.indexOf('android') === -1) {
    return 'Chrome';
  }

  if (ua.indexOf('safari') !== -1 && ua.indexOf('applewebkit') !== -1 && ua.indexOf('chrome') === -1 && ua.indexOf('mobile') === -1 && ua.indexOf('android') === -1) {
    return 'Safari';
  }

  if (ua.indexOf('safari') !== -1 && ua.indexOf('applewebkit') !== -1 && ua.indexOf('chrome') === -1 && ua.indexOf('mobile') !== -1 && ua.indexOf('android') === -1) {
    return 'Safari iOS';
  }

  return '';
}

function isTouchDevice(): boolean {
  return window.matchMedia('(hover: none)').matches;
}

export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function getOrganisationName(organisation: string | null | undefined): string {
  switch (organisation) {
    case 'HSB':
      return 'ARD Hauptstadtstudio';
    case 'DRADIO':
      return 'Deutschlandradio';
    case 'RBB':
    case 'HR':
      return organisation.toLowerCase();
    case 'IVZ':
    case 'BR':
    case 'DW':
    case 'MDR':
    case 'NDR':
    case 'SR':
    case 'SWR':
    case 'RB':
    case 'WDR':
      return organisation;
    default:
      return 'ARD';
  }
}

export function getOrganisationImageUrl(organisation: string | null | undefined): string {
  const baseUrl = 'https://tools.rbb-online.de/template/v4/images/';
  switch (organisation) {
    case 'RBB':
    case 'BR':
    case 'DRADIO':
    case 'DW':
    case 'HR':
    case 'MDR':
    case 'NDR':
    case 'SR':
    case 'SWR':
    case 'RB':
    case 'WDR':
      return `${baseUrl}${organisation.toLowerCase()}.svg`;
    case 'HSB':
    case 'IVZ':
      return `${baseUrl}${organisation.toLowerCase()}.webp`;
    default:
      return `${baseUrl}ard.svg`;
  }
}

(window as unknown as { getIdentifiedBrowserName: () => string }).getIdentifiedBrowserName = getIdentifiedBrowserName;
(window as unknown as { isTouchDevice: () => boolean }).isTouchDevice = isTouchDevice;
(window as unknown as { uuid: () => string }).uuid = uuid;
(window as unknown as { getOrganisationName: (organisation: string | null | undefined) => string }).getOrganisationName = getOrganisationName;
(window as unknown as { getOrganisationImageUrl: (organisation: string | null | undefined) => string }).getOrganisationImageUrl = getOrganisationImageUrl;

function generalObserver(): void {
  document.querySelectorAll('a[target="_blank"]:not([rel])').forEach(
    externalLink => externalLink.setAttribute('rel', 'noopener noreferrer')
  );
  document.querySelectorAll('a[href^="http"]:not([href^="' + window.location.origin + '"]):not(.external-link):not(.no-external-link), a[href^="mailto"]').forEach(
    (externalLink) => {
      if (!externalLink.querySelector('.link_extern_fa, .fa-external-link-alt') && externalLink.textContent) {
        externalLink.classList.add('external-link');
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  if (self === top) {
    document.body.style.display = 'block';
  } else if (top) {
    top.location = self.location;
  }

  if (isTouchDevice()) {
    document.body.classList.add('touch');
  }

  document.body.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      document.body.setAttribute('data-tabnav', 'true');
    }
  });

  document.body.addEventListener('mousedown', () => {
    document.body.removeAttribute('data-tabnav');
  });

  generalObserver();
  new MutationObserver(generalObserver).observe(document, { childList: true, subtree: true });
});
